import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UserCycleApi } from 'src/app/api/user-cycle.api';
import { CplRequestItem } from 'src/app/model/cpl-request-item.model';
import { CplUserRequest } from 'src/app/model/cpl-user-request.model';
import { CplCycle } from '../../../model/cpl-cycle.model';
import { CplItem } from '../../../model/cpl-item.model';
import { TimeTrackingTypes } from '../../../model/enum/cpl-time-tracking-type.enum';
import { CplUserStatus } from '../../compliance-items/compliance-item/enums/cpl-user-status.enum';
import { TimeTrackingHelperService } from 'src/app/time-tracking/services/time-tracking-helper/time-tracking-helper.service';

@Component({
    selector: 'flex-details',
    templateUrl: './flex-details.component.html',
    styleUrls: ['./flex-details.component.scss']
})
export class FlexDetailsComponent implements OnInit {
    @Input() userCycle: CplCycle;
    @Input() complianceItem: CplItem;
    @Input() isReadOnly: boolean = false;

    @Input() requestItem: CplRequestItem;
    @Input() userRequest: CplUserRequest;

    @Output() onSaveComplete = new EventEmitter<{ success: boolean }>();

    TimeTrackingType = TimeTrackingTypes;
    busy: boolean = false;

    // Local buffer for flex hours
    flexHours: number = 0;

    constructor(
        private timeTrackingHelperService: TimeTrackingHelperService,
        private userCycleApi: UserCycleApi,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        // Initialize buffer with current value
        this.isReadOnly = this.isReadOnly || this.isCompleted();
        this.flexHours =  !!this.userCycle ?
            this.timeTrackingHelperService.getUserCycleDisplayHours(this.userCycle, this.complianceItem)
            : this.timeTrackingHelperService.getUserRequestDisplayHours(this.userRequest, this.requestItem);
    }

    get userName(): string {
        return `${this.userCycle?.FirstName || ''} ${(this.userCycle?.LastName ?? this.userRequest.LastName) || ''}`.trim() || 'User';
    }

    get dueDateFormatted(): string {
        return this.userCycle?.EndDate ? new Date(this.userCycle.EndDate).toLocaleDateString() : 'N/A';
    }

    get complianceItemTitle(): string {
        return this.complianceItem?.Title || 'N/A';
    }

    canSave(): boolean {
        return !this.isReadOnly && !this.busy && typeof this.flexHours === 'number';
    }

    onSave(): void {
        if (!this.canSave()) {
            return;
        }
        if (!this.complianceItem?.ItemID || !this.userCycle?.CycleID) {
            console.error('Missing ItemID or CycleID for saving flex details.');
            this.onSaveComplete.emit({ success: false });
            return;
        }
        this.busy = true;
        const hours = this.flexHours;
        this.userCycleApi.updateFlexHoursForUserCycle(this.complianceItem.ItemID, this.userCycle.CycleID, hours)
            .subscribe({
                next: () => {
                    // Only update the model after successful save
                    if (this.userCycle.UserTimeTracking) {
                        this.userCycle.UserTimeTracking.Hours = hours;
                    }
                    this.busy = false;
                    this.toastr.success('Flex details saved successfully.', 'Save Successful');
                    this.onSaveComplete.emit({ success: true });
                },
                error: (error) => {
                    // HttpErrorHandler interceptor displays the error automatically
                    this.busy = false;
                    this.onSaveComplete.emit({ success: false });
                }
            });
    }

    onHoursInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        const num = Number(value);
        this.flexHours = value === '' || isNaN(num) ? 0 : num;
    }

    isCompleted(): boolean {
        const completedStatuses = [
            CplUserStatus.COMPLETE_NOTALL_APPROVED,
            CplUserStatus.VERIFIED,
            CplUserStatus.DECLINED,
            CplUserStatus.FORGIVEN
        ];
        return completedStatuses.includes(this.userCycle.UserStatus);
    }
}